import type { ModuleShell, ShellNotice } from '@krp/client-runtime'

import { aqsiPresence } from './features/catalog/api/catalogApi'
import type { CommandFeedback } from './shared/model/feedback'
import { feedbackCleared } from './shared/model/feedbackSlice'
import type { AqsiUiState } from './state'

/** Сигнал раз в минуту: служба считает человека ушедшим после пяти минут тишины (`Aqsi:Freshness:PresenceTimeoutMinutes`). */
export const AQSI_PRESENCE_INTERVAL_MS = 60_000

/** Итог «Готово» закрывается сам; отказ и «ответ не получен» остаются, пока человек их не закроет. */
const DONE_AUTO_CLOSE_MS = 6000

const ERROR_HINTS: Readonly<Record<string, string>> = {
  'shift-closed': 'Откройте или примите смену: «Действия» → «Смена».',
  'shift-not-adopted': 'Откройте или примите смену: «Действия» → «Смена».',
  'receipt-not-in-journal': 'Сверьте журнал чеков и повторите',
  'receipt-ambiguous': 'Подходит несколько чеков — укажите номер чека кассы из перечисленных выше',
  'operation-unresolved': 'Сначала дождитесь итога операции: проверьте статус или выполните сверку',
}

/** Итог команды окна AQSI как уведомление оболочки: тон, заголовок, подсказка по коду ошибки и код для администратора. */
export function feedbackNotice(item: CommandFeedback): ShellNotice {
  const hint = item.error === null ? null : ERROR_HINTS[item.error.code] ?? null
  return {
    tone: item.phase === 'rejected' ? 'danger' : item.phase === 'unknown' ? 'warning' : item.phase === 'sending' ? 'progress' : 'success',
    title:
      item.phase === 'rejected' ? 'Команда не выполнена' : item.phase === 'unknown' ? 'Ответ не получен' : item.phase === 'sending' ? 'Выполняется…' : 'Готово',
    text: item.error?.message,
    hint: item.note ?? hint,
    meta: item.error === null ? null : `Код: ${item.error.code}${item.error.correlationId === null ? '' : ` · запрос ${item.error.correlationId}`}`,
    autoCloseMs: item.phase === 'done' ? DONE_AUTO_CLOSE_MS : null,
  }
}

/**
 * Вклад AQSI в оболочку (RP-SEP5): итоги команд всех окон живут выше маршрута и появляются в общей области уведомлений;
 * «я в системе» (RP-SE13) запускает ОДНУ проверку кабинетов на всех - у AQSI нет веб-хуков на номенклатуру.
 */
export const aqsiShell: ModuleShell<AqsiUiState> = {
  notifications: {
    // Команды цикла оплаты сюда не пишут (RP-PAY-ONE, шаг 5): их ход, отказ и «ответ не получен» — в окне оплаты по центру.
    select: (state) => Object.fromEntries(Object.entries(state.feedback.byKey).map(([key, item]) => [key, feedbackNotice(item)])),
    clear: (key) => feedbackCleared({ key }),
  },
  presence: {
    intervalMs: AQSI_PRESENCE_INTERVAL_MS,
    ping: (dispatch) => dispatch(aqsiPresence.initiate()).unwrap(),
  },
}
